/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

/* eslint-disable no-duplicate-imports */
import { Module, Node } from '@msdyn365-commerce-modules/utilities';
import * as React from 'react';

import { IHelp, IOrderSummary, IPaymentMethods } from '@msdyn365-commerce-modules/order-management';
import { IGroup, IGroupDelivery, IGroups } from '@msdyn365-commerce-modules/order-management';
import { IOrderInformation } from '@msdyn365-commerce-modules/order-management';
import { ISalesLine } from '@msdyn365-commerce-modules/order-management';
import { IOrderConfirmationViewProps } from './hei-order-confirmation';
import { PepperjamPixel, BazzarvoiceTransactionPixel } from './components';
import { useGoogleAnalytics } from '../../common/useGoogleAnalytics';
import { AttributeTextValue, SalesOrder } from '@msdyn365-commerce/retail-proxy';
import { IRequestContext } from '@msdyn365-commerce/core';

export const OrderConfirmationOrderInfomation: React.FC<IOrderInformation> = ({
    orderInformationProps,
    receiptEmail,
    createdDate,
    channelReferenceId
}) => (
    <Node {...orderInformationProps}>
        {createdDate}
        {channelReferenceId}
        {receiptEmail}
    </Node>
);

export const OrderConfirmationSalesLine: React.FC<ISalesLine> = ({ salesLineProps, salesLine, buyAgainButton }) => (
    <Node {...salesLineProps}>{salesLine}</Node>
);

export const OrderConfirmationGroupDelivery: React.FC<IGroupDelivery & {
    salesOrder?: SalesOrder;
    isPickup: boolean;
    request: IRequestContext;
}> = ({ deliveryProps, heading, count, processing, address, trackingInfo, salesOrder, isPickup, pickupDateTimeslot, request }) => {
    const dlrName = React.useMemo(() => {
        if (isPickup) {
            return null;
        }

        const dlrAttr = salesOrder?.AttributeValues?.find(a => a.Name === 'DeliveryName') as AttributeTextValue;
        process.env.NODE_ENV === 'development' &&
            console.log('hei-order-confirmation.view', 'OrderConfirmationView', dlrAttr, salesOrder?.AttributeValues);
        const json = dlrAttr?.TextValue;
        if (json) {
            try {
                return JSON.parse(json) as { firstName: string; lastName: string };
            } catch {
                return null;
            }
        }

        return null;
    }, [isPickup, salesOrder]);

    const hasDlrName = dlrName?.firstName && dlrName?.lastName;
    if (hasDlrName) {
        deliveryProps.className = `${deliveryProps.className ?? ''} address-name-hidden`;
    }

    const pickupInfo = React.useMemo(() => {
        if (!isPickup) {
            return null;
        }

        const attrObj = salesOrder?.AttributeValues?.find(a => a.Name === 'PickupInfo') as AttributeTextValue;
        process.env.NODE_ENV === 'development' && console.log('hei-order-confirmation.view', 'OrderConfirmationView', attrObj);
        const json = attrObj?.TextValue;
        if (json) {
            try {
                return JSON.parse(json) as { name: string; notes: string };
            } catch {
                return null;
            }
        }

        return null;
    }, [isPickup, salesOrder]);

    let user: { firstName?: string; lastName?: string } = request.user;
    if (!hasDlrName && (!user.firstName || !user.lastName)) {
        if (pickupInfo?.name) {
            const names = pickupInfo.name.split(' ');
            user = {
                firstName: names.slice(0, -1).join(' ') || 'BOPIS',
                lastName: names.slice(-1).join(' ') || 'Guest'
            };
        } else {
            user = {
                firstName: 'BOPIS',
                lastName: 'Guest'
            };
        }
    }

    return (
        <Node {...deliveryProps}>
            {heading}
            {hasDlrName && (
                <span className='hei-delivery-name'>
                    <span className='hei-delivery-first-name'>{dlrName?.firstName}</span>{' '}
                    <span className='hei-delivery-last-name'>{dlrName?.lastName}</span>
                </span>
            )}
            {!hasDlrName && user.firstName && user.lastName && (
                <span className='d-none'>
                    <span className='hei-delivery-first-name'>{user.firstName}</span>
                    <span className='hei-delivery-last-name'>{user.lastName}</span>
                </span>
            )}
            {pickupInfo?.name && <span className='hei-pickup-name'>{pickupInfo.name}</span>}
            {address}
            {pickupDateTimeslot}
        </Node>
    );
};

export const OrderConfirmationGroup: React.FC<IGroup & { salesOrder?: SalesOrder; request: IRequestContext }> = ({
    request,
    groupProps,
    delivery,
    salesLinesProps,
    salesLines,
    salesOrder
}) => {
    const isPickup = !!salesLines.find(sl => sl.data.deliveryType === 'Pick-up');
    return (
        <Node {...groupProps}>
            {delivery && <OrderConfirmationGroupDelivery {...delivery} salesOrder={salesOrder} isPickup={isPickup} request={request} />}
            {delivery && delivery.shippingItemsToYou}
            {salesLines && (
                <Node {...salesLinesProps}>
                    {salesLines.map((salesLine: ISalesLine) => (
                        <OrderConfirmationSalesLine key={salesLine.data.salesLine.LineId} {...salesLine} />
                    ))}
                </Node>
            )}
        </Node>
    );
};

export const OrderConfirmationGroups: React.FC<IGroups & { salesOrder?: SalesOrder; request: IRequestContext }> = ({
    request,
    groupsProps,
    groups,
    salesOrder
}) => (
    <Node {...groupsProps}>
        {groups.map((group: IGroup, index: number) => (
            <OrderConfirmationGroup key={index} {...group} salesOrder={salesOrder} request={request} />
        ))}
    </Node>
);

export const OrderConfirmationOrderSummary: React.FC<IOrderSummary> = ({
    orderSummaryProps,
    heading,
    subtotal,
    shipping,
    tax,
    totalAmount,
    earnedPoints
}) => (
    <Node {...orderSummaryProps}>
        {heading}
        {subtotal}
        {shipping}
        {tax}
        {totalAmount}
        {earnedPoints}
    </Node>
);

export const OrderConfirmationPayment: React.FC<IPaymentMethods> = ({ paymentMethodsProps, title, methods }) => (
    <Node {...paymentMethodsProps}>
        {title}
        {methods}
    </Node>
);

export const OrderConfirmationHelp: React.FC<IHelp> = ({ helpProps, needHelpLabel, helpLineNumberLabel, contactNumber }) => (
    <Node {...helpProps}>
        {needHelpLabel}
        {helpLineNumberLabel}
        {contactNumber}
    </Node>
);

const OrderConfirmationView: React.FC<IOrderConfirmationViewProps> = ({
    moduleProps,
    heading,
    backToShoppingLink,
    alert,
    loading,
    orderInfomation,
    orderSummary,
    payment,
    help,
    groups,
    salesOrder,
    pepperjam,
    telemetry,
    gtmData,
    gaPayload,
    context: { request, actionContext }
}) => {
    const gaCached = React.useMemo(() => gaPayload, [gaPayload?.ecommerce?.transaction_id ?? '']);
    useGoogleAnalytics(gaCached, actionContext);

    return (
        <Module {...moduleProps}>
            {heading}
            {alert}
            {loading}
            {orderInfomation && <OrderConfirmationOrderInfomation {...orderInfomation} />}
            {backToShoppingLink}
            {groups && <OrderConfirmationGroups {...groups} salesOrder={salesOrder} request={request} />}
            {payment && <OrderConfirmationPayment {...payment} />}
            {orderSummary && <OrderConfirmationOrderSummary {...orderSummary} />}
            {help && <OrderConfirmationHelp {...help} />}
            {salesOrder && <PepperjamPixel order={salesOrder} config={pepperjam} request={request} telemetry={telemetry} />}
            {salesOrder && <BazzarvoiceTransactionPixel order={salesOrder} locale={request.locale} />}

            {gtmData && (
                <script className='hei-gtm-order-confirmation' type='application/json'>
                    {JSON.stringify(gtmData)}
                </script>
            )}
        </Module>
    );
};

export default OrderConfirmationView;
